import React from "react"

import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { type VehicleTypeFormData } from "@/lib/configForm"
import { numberOrNull } from "@/lib/utils"
import { DrivingRegime } from "@/types/collections"

import NumberField from "./NumberField"

/** Select value standing in for a null regime; a Select item cannot carry an empty value. */
const REGIME_UNSET = "unset"

const CAPACITY_FIELDS = [
  { key: "eur_pallet_capacity", label: "EUR Pallets" },
  { key: "uk_pallet_capacity", label: "UK Pallets" },
  { key: "cage_capacity", label: "Cages" },
  { key: "magnum_capacity", label: "Magnums" },
  { key: "sort_bag_capacity", label: "Sort Bags" },
] as const

interface VehicleTypeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When editing, the name is fixed. */
  isEditing: boolean
  form: VehicleTypeFormData
  setForm: (form: VehicleTypeFormData) => void
  /** On Site £/kWh from Global Config ÷ the form's miles per kWh. */
  chargingCostPerMile: number
  onSave: () => void
  isSaving: boolean
}

const VehicleTypeDialog: React.FC<VehicleTypeDialogProps> = ({
  open,
  onOpenChange,
  isEditing,
  form,
  setForm,
  chargingCostPerMile,
  onSave,
  isSaving,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{isEditing ? "Edit Vehicle Type" : "Add Vehicle Type"}</DialogTitle>
        <DialogDescription>
          Configure the cost parameters for this vehicle type. Charging cost per mile is
          automatically calculated.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., HGV-Curtainsider-350mi"
            disabled={isEditing}
          />
          {isEditing && (
            <p className="text-xs text-palette-jungleGreen500">
              Vehicle type name cannot be changed as it's used in existing routes
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="Optional friendly name"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <NumberField
            id="monthly_lease_cost"
            label="Monthly Lease Cost (£) *"
            className="space-y-2"
            step="0.01"
            min="0"
            value={form.monthly_lease_cost}
            onChange={(raw) => setForm({ ...form, monthly_lease_cost: parseFloat(raw) || 0 })}
          />
          <NumberField
            id="miles_per_kwh"
            label="Miles per kWh *"
            className="space-y-2"
            step="0.01"
            min="0.01"
            value={form.miles_per_kwh}
            onChange={(raw) => setForm({ ...form, miles_per_kwh: parseFloat(raw) || 0 })}
          />
        </div>

        <div className="bg-palette-jungleGreen000 border border-palette-jungleGreen200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Charging Cost per Mile (On Site)</Label>
            <span className="text-lg font-semibold text-palette-jungleGreen900">
              £{chargingCostPerMile.toFixed(4)}
            </span>
          </div>
          <p className="text-xs text-palette-jungleGreen500 mt-1">
            From Global Config: On Site Charging Cost (internal) ÷ Miles per kWh
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <NumberField
            id="labour_hourly_rate"
            label="Labour Hourly Rate (£) *"
            className="space-y-2"
            step="0.01"
            min="0"
            value={form.labour_hourly_rate}
            onChange={(raw) => setForm({ ...form, labour_hourly_rate: parseFloat(raw) || 0 })}
          />
          <NumberField
            id="range_miles"
            label="Range (miles) *"
            className="space-y-2"
            step="1"
            min="1"
            value={form.range_miles}
            onChange={(raw) => {
              const v = parseFloat(raw)
              setForm({ ...form, range_miles: Number.isNaN(v) || v < 1 ? 100 : v })
            }}
          />
        </div>

        <Separator />

        <div className="space-y-4">
          <div>
            <Label className="text-base font-medium">Vehicle Capacity (Loading Units)</Label>
            <p className="text-xs text-palette-jungleGreen500 mt-1 mb-3">
              Maximum number of each loading unit type this vehicle can carry
            </p>
            <div className="grid grid-cols-2 gap-4">
              {CAPACITY_FIELDS.map(({ key, label }) => (
                <NumberField
                  key={key}
                  id={key}
                  label={label}
                  className="space-y-2"
                  step="1"
                  min="0"
                  value={form[key] || 0}
                  onChange={(raw) => setForm({ ...form, [key]: parseInt(raw, 10) || 0 })}
                />
              ))}
            </div>
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="driving_regime">Driving regime</Label>
            <Select
              value={form.driving_regime ?? REGIME_UNSET}
              onValueChange={(v) =>
                setForm({
                  ...form,
                  driving_regime: v === REGIME_UNSET ? null : (v as DrivingRegime),
                })
              }
            >
              <SelectTrigger id="driving_regime">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={REGIME_UNSET}>Not set</SelectItem>
                <SelectItem value="assimilated">Assimilated (over 3.5 t)</SelectItem>
                <SelectItem value="gb_domestic">GB domestic (up to 3.5 t)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-palette-jungleGreen500">
              Not set: HGV names are assimilated
            </p>
          </div>
          <NumberField
            id="height_m"
            label="Height (m)"
            className="space-y-2"
            step="0.01"
            min="0"
            placeholder="Unknown"
            value={form.height_m ?? ""}
            onChange={(raw) => setForm({ ...form, height_m: numberOrNull(raw) })}
          />
          <NumberField
            id="gross_weight_kg"
            label="Gross Weight (kg)"
            className="space-y-2"
            step="1"
            min="0"
            placeholder="Unknown"
            value={form.gross_weight_kg ?? ""}
            onChange={(raw) => setForm({ ...form, gross_weight_kg: numberOrNull(raw) })}
          />
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="is_active">Active</Label>
            <p className="text-xs text-palette-jungleGreen500">
              Only active vehicle types are available for selection
            </p>
          </div>
          <Switch
            id="is_active"
            checked={form.is_active}
            onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
          />
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button variant="confirm" onClick={onSave} disabled={isSaving || !form.name.trim()}>
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isEditing ? "Update" : "Create"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)

export default VehicleTypeDialog
